// =============================================================================
// VINUM - Order API
// Migrated from Flutter OrderAPI with EXACT endpoints
// =============================================================================

import { apiGet, apiPost, apiPut } from './client';
import type { Order, OrderFilter, OrderResponse, OrderStatus, Address } from '../types';

// -----------------------------------------------------------------------------
// Order API Endpoints
// Exact match with Flutter order_api.dart
// -----------------------------------------------------------------------------

export const OrderAPI = {
  /**
   * Get orders for current user with filters
   * GET /orders
   */
  async getOrders(filter?: OrderFilter): Promise<OrderResponse> {
    const params: Record<string, unknown> = {};

    if (filter) {
      if (filter.status && filter.status !== 'all') {
        params.status = filter.status;
      }
      if (filter.startDate) params.startDate = filter.startDate;
      if (filter.endDate) params.endDate = filter.endDate;
      if (filter.page != null) params.page = filter.page;
      if (filter.limit != null) params.limit = filter.limit;
    }

    return apiGet('/orders', { params });
  },

  /**
   * Get single order by ID
   * GET /orders/:id
   */
  async getOrder(id: string): Promise<Order> {
    return apiGet(`/orders/${id}`);
  },

  /**
   * Get order by order number
   * GET /orders/number/:orderNumber
   */
  async getOrderByNumber(orderNumber: string): Promise<Order> {
    return apiGet(`/orders/number/${orderNumber}`);
  },

  /**
   * Create new order from cart
   * POST /orders
   * Backend expects: { customerId, deliveryMethodId, paymentMethodId, total }
   */
  async createOrder(data: {
    customerId: string;
    deliveryMethodId: string;
    paymentMethodId: string;
    total?: number;
    notes?: string;
  }): Promise<Order> {
    return apiPost('/orders', data);
  },

  /**
   * Create order with specific items (bypassing cart)
   * POST /orders/direct
   */
  async createDirectOrder(data: {
    items: Array<{
      itemId: string;
      name: string;
      quantity: number;
      unitPrice: number;
    }>;
    shippingAddress?: Address;
    billingAddress?: Address;
    notes?: string;
  }): Promise<Order> {
    return apiPost('/orders/direct', data);
  },

  /**
   * Update order status (admin)
   * PUT /orders/:id/status
   */
  async updateStatus(id: string, status: OrderStatus): Promise<Order> {
    return apiPut(`/orders/${id}/status`, { status });
  },

  /**
   * Cancel order
   * POST /orders/:id/cancel
   */
  async cancelOrder(id: string, reason?: string): Promise<Order> {
    return apiPost(`/orders/${id}/cancel`, { reason });
  },

  /**
   * Reorder (copy items to cart)
   * POST /orders/:id/reorder
   */
  async reorder(id: string): Promise<{ message: string }> {
    return apiPost(`/orders/${id}/reorder`);
  },

  /**
   * Get order statistics
   * GET /orders/statistics
   */
  async getStatistics(): Promise<{
    totalOrders: number;
    pendingOrders: number;
    completedOrders: number;
    cancelledOrders: number;
  }> {
    return apiGet('/orders/statistics');
  },

  /**
   * Get recent orders
   * GET /orders/recent
   */
  async getRecentOrders(limit = 5): Promise<Order[]> {
    const response = await apiGet<OrderResponse>('/orders/recent', {
      params: { limit },
    });
    return response.orders;
  },

  /**
   * Download order invoice (PDF)
   * GET /orders/:id/invoice
   */
  async getInvoiceUrl(id: string): Promise<string> {
    const response = await apiGet<{ url: string }>(`/orders/${id}/invoice`);
    return response.url;
  },

  /**
   * Add note to order
   * POST /orders/:id/notes
   */
  async addNote(id: string, note: string): Promise<Order> {
    return apiPost(`/orders/${id}/notes`, { note });
  },

  /**
   * Track order
   * GET /orders/:id/tracking
   */
  async getTracking(id: string): Promise<{
    status: OrderStatus;
    history: Array<{
      status: OrderStatus;
      timestamp: string;
      description?: string;
    }>;
    estimatedDelivery?: string;
  }> {
    return apiGet(`/orders/${id}/tracking`);
  },

  /**
   * Get orders by customer ID
   * GET /orders/customer/:customerId
   */
  async getByCustomerId(customerId: string): Promise<Order[]> {
    return apiGet(`/orders/customer/${customerId}`);
  },

  /**
   * Get orders placed personally by the logged-in user (filtered server-side
   * via OrderCartItem.userId). Use this for the "I miei ordini" customer view
   * so a user belonging to a multi-user Customer sees only the orders they
   * actually placed.
   * GET /orders/my
   */
  async getMy(): Promise<Order[]> {
    return apiGet('/orders/my');
  },
};
